"use client";

import styled from "styled-components";
import { Icon } from "../Icon/Icon";
import NotificationIcon from "@/assets/icons/Notifications.svg";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import theme from "@/themes/velys-theme";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useMedia } from "@/lib/hooks/useMedia";
import { useTranslations } from "next-intl";
import { Modal } from "../Modal/Modal";
import { Dropdown } from "../Dropdown/Dropdown";
import { useEffect, useMemo, useState } from "react";
import Button from "../Button/Button";
import { NotificationItem } from "./NotificationItem";
import { useNotifications } from "@/lib/hooks/useNotifications";
import { Notification } from "@clients/velys-infrastructure/api";
import CaseDataLoader from "../CaseDataLoader/CaseDataLoader";
import { useIntersectionObserver } from "@/lib/hooks/useIntersectionObserver";

//#region styled
const NotificationContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 20px;
  padding-bottom: 5px;
  .toggle-category {
    cursor: pointer;
  }

  .count {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: ${(props) => props.theme.white};
    color: ${(props) => props.theme.main};
    border: 1px solid ${(props) => props.theme.main};
    font-size: 10px;
    font-weight: bold;
    min-width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0 3px;

    &.more {
      font-size: 9px;
      min-width: 18px;
      height: 18px;
      padding: 0 1.5px;
    }
  }

  .notifications-list {
    position: absolute;
    top: 40px;
    right: -12px;
    margin-top: 8px;
    background-color: white;
    border: 1px solid white;
    border-radius: 3px;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.15);
    min-width: 460px;
    max-height: fit-content;
    overflow-y: auto;
    z-index: 998;
    overflow: visible;
    padding: 15px;

    &::after {
      content: "";
      position: absolute;
      top: -19px;
      right: 9px;
      border-left: 12px solid transparent;
      border-right: 12px solid transparent;
      border-bottom: 18px solid rgba(0, 0, 0, 0.05);
      filter: blur(0.5px);
      z-index: 0;
    }

    &::before {
      content: "";
      position: absolute;
      top: -18px;
      right: 10px;
      border-left: 11px solid transparent;
      border-right: 11px solid transparent;
      border-bottom: 18px solid #fff;
      z-index: 1;
    }
  }

  .cursor-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
  }

  @media (max-width: 1024px) {
    margin: 0;
    padding: 0;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.lightGrey};
  padding-top: 5px;
  padding-bottom: 15px;

  h2 {
    color: ${(props) => props.theme.main};
  }
`;

const NotificationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9998;
`;

const NotificationSidebar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 450px;
  background-color: #fff;
  border-left: 1px solid ${(props) => props.theme.cardBorder};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 9998;
  padding: 20px;
  padding-right: 15px;

  .sidebar-list {
    height: 85vh;
    padding-right: 10px;
    overflow-y: auto;
  }
`;

const NotificationModal = styled.div`
  width: 450px;
  min-height: 200px;
  margin: 10px 0px;
  margin-bottom: 20px;
  padding-left: 10px;

  .modal-list {
    max-height: 450px;
    overflow-y: auto;
    padding-right: 10px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

//#endregion

export default function Notifications() {
  const {
    orgNotificationQuery,
    orgNotificationInfiniteQuery,
    currentNotifications,
    deleteNotificationMutation,
    isLoadingPatientNames,
    patientNamesMap,
    setInfiniteQueryEnabled,
  } = useNotifications();
  const tNotifications = useTranslations("notifications");
  const openNotifications = useBoundStore((state) => state.openNotifications);
  const setOpenNotifications = useBoundStore(
    (state) => state.setOpenNotifications
  );
  const openAllNotifs = useBoundStore((state) => state.openAllNotifs);
  const setOpenAllNotifs = useBoundStore((state) => state.setOpenAllNotifs);
  const currentNotificationClearIds = useBoundStore(
    (state) => state.currentNotificationClearIds
  );

  const [alertType, setAlertType] = useState("all");
  const max1024w = useMedia("(max-width: 1024px)");
  const max550w = useMedia("(max-width: 550px)");

  useEffect(() => {
    if (openNotifications || openAllNotifs) {
      setInfiniteQueryEnabled(true);
    } else {
      setInfiniteQueryEnabled(false);
    }
  }, [openNotifications, openAllNotifs, setInfiniteQueryEnabled]);

  useEffect(() => {
    if (max1024w) {
      if (openAllNotifs || openNotifications) {
        setOpenAllNotifs(false);
        setOpenNotifications(true);
      }
    }
  }, [max1024w]);

  const filteredNotifications = useMemo(() => {
    if (!currentNotifications.length) {
      return [];
    }

    if (alertType === "pain") {
      return currentNotifications.filter(
        (notif) => notif.notification_slug === "ASSESSMENT_PAIN"
      );
    } else if (alertType === "incision") {
      return currentNotifications.filter(
        (notif) => notif.notification_slug === "PHOTO_UPLOADED"
      );
    } else if (alertType === "pre-op") {
      return currentNotifications.filter(
        (notif) => notif.notification_slug === "PRE_OP_PHOTO_UPLOADED"
      );
    }
    return currentNotifications;
  }, [alertType, currentNotifications]);

  const badgeCount = useMemo(() => {
    return orgNotificationQuery.data?.nextCursor
      ? "10+"
      : currentNotifications.length;
  }, [orgNotificationQuery.data, currentNotifications]);

  const notificationsCursor =
    orgNotificationInfiniteQuery.data?.pages?.[
      orgNotificationInfiniteQuery.data.pages.length - 1
    ]?.nextCursor ?? orgNotificationQuery.data?.nextCursor;

  const alertTypes = [
    {
      value: "all",
      name: tNotifications("all"),
    },
    {
      value: "pain",
      name: tNotifications("highPain"),
    },
    {
      value: "incision",
      name: tNotifications("incision"),
    },
    {
      value: "pre-op",
      name: tNotifications("pre-op"),
    },
  ];

  const extractEnrollmentId = (url: string) => {
    const parts = url.split("/");
    const index = parts.indexOf("enrollments");
    return index !== -1 ? parts[index + 1] : undefined;
  };

  const handleDismissNotif = (notifId: string) => {
    deleteNotificationMutation.mutate(notifId);
  };

  const viewMoreNotifs = () => {
    setOpenAllNotifs(!openAllNotifs);
    setOpenNotifications(!openNotifications);
  };

  const renderNotifications = (list: Notification[]) => {
    return list.map((notif) => {
      const enrollmentId = extractEnrollmentId(notif.notification_uri) || "";
      const isLoadingName =
        isLoadingPatientNames && !patientNamesMap[enrollmentId];

      return (
        <NotificationItem
          key={notif.id}
          notif={notif}
          patientName={patientNamesMap[enrollmentId] || "N/A"}
          enrollmentId={enrollmentId}
          handleDismissNotif={handleDismissNotif}
          isLoadingName={isLoadingName}
          isPending={
            currentNotificationClearIds.includes(notif.id) &&
            deleteNotificationMutation.isPending
          }
        />
      );
    });
  };
  // Intersection observer for infinite scroll
  const notificationObserverRef = useIntersectionObserver({
    callback: () => {
      orgNotificationInfiniteQuery.fetchNextPage();
    },
    cursor: notificationsCursor,
  });
  return (
    <NotificationContainer>
      <Icon
        customClass="toggle-category"
        url={NotificationIcon.src}
        color={theme.main}
        height="22px"
        width="22px"
        onPress={() => setOpenNotifications(!openNotifications)}
      />
      <div
        className={`count ${orgNotificationQuery.data?.nextCursor ? "more" : ""}`}
        onClick={() => setOpenNotifications(!openNotifications)}
      >
        {badgeCount}
      </div>
      {openNotifications &&
        (!max1024w ? (
          <div className="notifications-list">
            <NotificationHeader>
              <h2>{`${alertType === "all" ? badgeCount : filteredNotifications.length} ${tNotifications("new")} ${tNotifications("title")}`}</h2>
              <Dropdown
                inModal
                styles={{ minWidth: "unset", maxWidth: "180px" }}
                dropdownTitle={tNotifications("alertType")}
                options={alertTypes}
                onSelect={(e) => {
                  const value = (e.target as HTMLLIElement).getAttribute(
                    "data-value"
                  );
                  setAlertType(value!);
                }}
                color={theme.main}
                backgroundColor={theme.white}
                selectedOption={alertType}
              />
            </NotificationHeader>
            {renderNotifications(filteredNotifications.slice(0, 5))}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 15,
              }}
            >
              {filteredNotifications && filteredNotifications.length > 5 && (
                <Button
                  label={tNotifications("viewAdditionalNotifications")}
                  theme="tertiary"
                  name="more-notifs-button"
                  onPress={() => viewMoreNotifs()}
                />
              )}
            </div>
          </div>
        ) : (
          <Modal
            modalWidth={max550w ? "90%" : "revert"}
            title={tNotifications("title")}
            showtimesIcon
            content={
              <NotificationModal>
                <Dropdown
                  inModal
                  styles={{
                    minWidth: "unset",
                    maxWidth: "180px",
                    marginTop: 20,
                    marginBottom: 10,
                  }}
                  dropdownTitle={tNotifications("alertType")}
                  options={alertTypes}
                  onSelect={(e) => {
                    const value = (e.target as HTMLLIElement).getAttribute(
                      "data-value"
                    );
                    setAlertType(value!);
                  }}
                  color={theme.main}
                  backgroundColor={theme.white}
                  selectedOption={alertType}
                />
                <div className="modal-list">
                  {renderNotifications(filteredNotifications)}
                  {orgNotificationInfiniteQuery.hasNextPage && (
                    <>
                      <div
                        ref={notificationObserverRef}
                        style={{
                          visibility: "hidden",
                          height: 0,
                          padding: 0,
                          minHeight: 0,
                        }}
                      />
                      {orgNotificationInfiniteQuery.isFetchingNextPage && (
                        <div className="cursor-loader">
                          <CaseDataLoader percent={null} size="46px" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </NotificationModal>
            }
            toggleModal={() => setOpenNotifications(!openNotifications)}
          />
        ))}

      {/* Right sidebar for all notifications */}
      {openAllNotifs && (
        <>
          <NotificationOverlay />
          <NotificationSidebar>
            <NotificationHeader>
              <h2>{`${alertType === "all" ? badgeCount : filteredNotifications.length} ${tNotifications("new")} ${tNotifications("title")}`}</h2>
              <Icon
                customClass="close-all-notifs"
                url={CloseIcon.src}
                color={theme.main}
                height="20px"
                width="20px"
                onPress={() => setOpenAllNotifs(false)}
              />
            </NotificationHeader>
            <Dropdown
              inModal
              styles={{
                minWidth: "unset",
                maxWidth: "180px",
                marginTop: 20,
                marginBottom: 10,
              }}
              dropdownTitle={tNotifications("alertType")}
              options={alertTypes}
              onSelect={(e) => {
                const value = (e.target as HTMLLIElement).getAttribute(
                  "data-value"
                );
                setAlertType(value!);
              }}
              color={theme.main}
              backgroundColor={theme.white}
              selectedOption={alertType}
            />
            <div className="sidebar-list">
              {renderNotifications(filteredNotifications)}
              {orgNotificationInfiniteQuery.hasNextPage && (
                <>
                  <div
                    ref={notificationObserverRef}
                    style={{
                      visibility: "hidden",
                      height: 0,
                      padding: 0,
                      minHeight: 0,
                    }}
                  />
                  {orgNotificationInfiniteQuery.isFetchingNextPage && (
                    <div className="cursor-loader">
                      <CaseDataLoader percent={null} size="46px" />
                    </div>
                  )}
                </>
              )}
            </div>
          </NotificationSidebar>
        </>
      )}
    </NotificationContainer>
  );
}
